/**
 * useProjectQueries.ts
 * 
 * Standalone React Query hooks for Projects data.
 * 
 * ProjectsContext.tsx internally delegates to these hooks for backward compatibility.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTallerListo } from './useTallerListo';
import { Project } from '../types';
import {
    fetchProjects, addProjectDoc, updateProjectDoc, deleteProjectDoc, transitionProjectStatusDoc, appendProjectNoteDoc,
    appendProjectTaskDoc, patchProjectTaskDoc, removeProjectTaskDoc
} from '../services/database';

export const PROJECT_KEYS = {
    projects: (wsId: string) => ['projects', wsId] as const,
};

// La puerta de arranque (`useTallerListo`) vive en hooks/useTallerListo.ts,
// compartida con flota, servicios, reloj y estimados.
export function useProjectsQuery() {
    const { wsId, listo } = useTallerListo();

    return useQuery({
        queryKey: PROJECT_KEYS.projects(wsId),
        // Flecha y no `queryFn: fetchProjects`: React Query llama a la función
        // con su contexto como primer argumento, y por referencia ese objeto
        // acabaría en el parámetro del taller.
        queryFn: () => fetchProjects(wsId),
        enabled: listo,
    });
}

export function useAddProject() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (project: Omit<Project, 'id' | 'assignedCrewIds' | 'completionPercent' | 'tasks' | 'materialStatus' | 'changeOrders' | 'notes' | 'estimatedLaborHours' | 'expenses' | 'invoices'>) => {
            return await addProjectDoc(project);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
    });
}

export function useUpdateProject() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, updates }: { id: string; updates: Partial<Project> }) => {
            await updateProjectDoc(id, updates);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
    });
}

export function useTransitionProjectStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, status }: { id: string; status: string }) => {
            await transitionProjectStatusDoc(id, status);
        },
        // Completing/cancelling frees the job's equipment server-side, so refresh the
        // fleet cache too (not just projects).
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['projects'] });
            qc.invalidateQueries({ queryKey: ['equipment'] });
        },
    });
}

export function useAppendProjectNote() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, note }: { id: string; note: any }) => {
            await appendProjectNoteDoc(id, note);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
    });
}

export function useAppendProjectTask() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, task }: { id: string; task: any }) => { await appendProjectTaskDoc(id, task); },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
    });
}

export function usePatchProjectTask() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, taskId, patch }: { id: string; taskId: string; patch: any }) => { await patchProjectTaskDoc(id, taskId, patch); },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
    });
}

export function useRemoveProjectTask() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, taskId }: { id: string; taskId: string }) => { await removeProjectTaskDoc(id, taskId); },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
    });
}

export function useDeleteProject() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await deleteProjectDoc(id);
        },
        // Cascade frees equipment + unlinks appointments — refresh those caches too.
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['projects'] });
            qc.invalidateQueries({ queryKey: ['equipment'] });
            qc.invalidateQueries({ queryKey: ['appointments'] });
        },
    });
}
